import {
  Link,
  useParams,
  useMatches,
  useNavigate,
} from "@tanstack/react-router";
import { useAuth, UserButton } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { Select } from "@base-ui/react/select";
import {
  IconLayoutDashboard,
  IconUsers,
  IconCalendarEvent,
  IconReceipt,
  IconUserPlus,
  IconRobot,
  IconChevronDown,
  IconCheck,
  IconPlus,
} from "@tabler/icons-react";
import cobbleLogo from "../../cobbleLogo.svg";
import { organizationsQueryOptions } from "../../api/queries";
import { createOrgSlug, parseOrgId } from "../../lib/slug";

const navItems = [
  { segment: "overview", label: "Overview", icon: IconLayoutDashboard },
  { segment: "members", label: "Members", icon: IconUsers },
  { segment: "events", label: "Events", icon: IconCalendarEvent },
  { segment: "expenses", label: "Expenses", icon: IconReceipt },
  { segment: "recruitment", label: "Recruitment", icon: IconUserPlus },
  { segment: "automations", label: "Automations", icon: IconRobot },
] as const;

export function Sidebar() {
  const { getToken } = useAuth();
  const { data, isLoading } = useQuery(organizationsQueryOptions(getToken));
  const orgs = data?.organizations ?? [];

  const { slug } = useParams({ from: "/organization/$slug" });
  const navigate = useNavigate();
  const matches = useMatches();

  const currentOrgId = parseOrgId(slug);
  const currentOrg = orgs.find((o) => o.organizationId === currentOrgId);

  // Determine the current leaf segment (e.g. "overview", "members")
  const leafMatch = matches[matches.length - 1];
  const currentSegment =
    leafMatch?.pathname.split("/").filter(Boolean).pop() ?? "overview";

  function handleOrgChange(value: string | null) {
    if (!value) return;
    if (value === "new-org") {
      navigate({ to: "/organization" });
      return;
    }
    const org = orgs.find((o) => o.organizationId === value);
    if (org) {
      const newSlug = createOrgSlug(org.organizationId, org.name);
      navigate({
        to: `/organization/$slug/${currentSegment}` as "/organization/$slug/overview",
        params: { slug: newSlug },
      });
    }
  }

  return (
    <aside className="flex flex-col w-60 shrink-0 border-r border-gray-200 bg-white h-screen sticky top-0">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 h-16 shrink-0">
        <img src={cobbleLogo} alt="Cobble logo" className="h-8 w-8" />
        <span className="text-lg font-semibold text-gray-900">Cobble</span>
      </div>

      {/* Organization selector */}
      <div className="px-3 mb-4">
        <Select.Root
          value={currentOrg?.organizationId}
          onValueChange={handleOrgChange}
        >
          <Select.Trigger className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
            <Select.Value
              placeholder={isLoading ? "Loading\u2026" : "Select organization"}
            >
              {currentOrg?.name}
            </Select.Value>
            <Select.Icon>
              <IconChevronDown size={16} className="text-gray-400" />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Positioner sideOffset={4}>
              <Select.Popup className="min-w-[var(--anchor-width)] rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {orgs.map((org) => (
                  <Select.Item
                    key={org.organizationId}
                    value={org.organizationId}
                    className="flex items-center gap-2 cursor-pointer px-3 py-2 text-sm text-gray-700 data-[highlighted]:bg-gray-100"
                  >
                    <Select.ItemIndicator className="inline-flex">
                      <IconCheck size={14} />
                    </Select.ItemIndicator>
                    <Select.ItemText>{org.name}</Select.ItemText>
                  </Select.Item>
                ))}
                <Select.Item
                  value="new-org"
                  className="flex items-center gap-2 cursor-pointer px-3 py-2 text-sm text-gray-700 border-t border-gray-200 data-[highlighted]:bg-gray-100"
                >
                  <IconPlus size={14} />
                  <Select.ItemText>New organization</Select.ItemText>
                </Select.Item>
              </Select.Popup>
            </Select.Positioner>
          </Select.Portal>
        </Select.Root>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link
            key={item.segment}
            to={
              `/organization/$slug/${item.segment}` as "/organization/$slug/overview"
            }
            params={{ slug }}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            activeProps={{
              className:
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-primary-darker",
            }}
          >
            <item.icon size={20} stroke={1.5} />
            {item.label}
          </Link>
        ))}
      </nav>

      {/* User button */}
      <div className="px-5 py-4 border-t border-gray-200 flex flex-row gap-2 items-center justify-between">
        <p className="text-sm text-gray-500">
          Signed in as{" "}
          <span className="font-semibold text-primary-darker">Owner</span>
        </p>
        <UserButton />
      </div>
    </aside>
  );
}
